import { useTranslation, Trans } from 'react-i18next';
import * as Styled from './CallToActionButton.styled';
import { checkoutUrlBuilder } from '../../../features/checkout/checkoutUrlBuilder';
import { useAddCartItem } from '../../../features/cart/useAddCartItem';
import { PRODUCT_TYPES } from '../../../features/cart/cart.model';
import { FREQUENCY } from '../../../models/DonationFrequency';
import { Add, Check } from '../../../svgs';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
const CartButton = _ref => {
  let {
    blockState,
    programDesignation,
    error = false,
    isLoading
  } = _ref;
  const {
    t
  } = useTranslation('blocks.donation');
  const {
    dispatchNewCartItem,
    cartIsOpen
  } = useAddCartItem();
  const isDisabled = error || !blockState.amount || isLoading;
  const handleAddCartItem = () => {
    const cartItem = {
      designationId: programDesignation?.id,
      name: programDesignation?.name || t('CallToActionButton.cartItem.generalFund', 'General Fund'),
      productType: blockState.frequency !== FREQUENCY.ONE_TIME ? PRODUCT_TYPES.RECURRING : PRODUCT_TYPES.DONATION,
      qty: 1,
      unitPrice: blockState?.amount ?? 0
    };
    if (cartItem.productType === PRODUCT_TYPES.RECURRING) {
      // * Get user's current timezone
      const {
        timeZone
      } = Intl.DateTimeFormat().resolvedOptions();

      // * Only add endDate if user selects one
      const endDate = blockState.recurringEndDate && {
        endDate: blockState.recurringEndDate
      };

      // Note, start date will be assumed to be today, which is why it is not included.
      cartItem.recurringProperties = {
        frequency: blockState.frequency,
        timeZone,
        ...endDate
      };
    }
    dispatchNewCartItem(cartItem);
  };
  let label = /*#__PURE__*/_jsx(_Fragment, {
    children: t('CallToActionButton.cartButton.label.chooseAmount', 'Choose an amount')
  });
  if (!isDisabled) {
    if (!cartIsOpen) {
      label = /*#__PURE__*/_jsx(Trans, {
        t: t,
        i18nKey: "CallToActionButton.cartButton.label.addToDonation",
        components: {
          icon: /*#__PURE__*/_jsx(Add, {})
        },
        defaults: "Add to donation"
      });
    } else {
      label = /*#__PURE__*/_jsx(Trans, {
        t: t,
        i18nKey: "CallToActionButton.cartButton.label.added",
        components: {
          icon: /*#__PURE__*/_jsx(Check, {})
        },
        defaults: "Added"
      });
    }
  }
  return /*#__PURE__*/_jsx(Styled.CartButton, {
    type: "button",
    "data-id": "cp-donation-form-donate",
    isDisabled: isDisabled,
    onClick: !isDisabled ? handleAddCartItem : undefined,
    children: label
  });
};
const CheckoutLinkButton = _ref2 => {
  let {
    blockState,
    campaignId = '',
    orgId = '',
    forwardCheckoutQueryParams = {},
    checkoutBaseUrl = '',
    error = false,
    isLoading,
    programDesignation
  } = _ref2;
  const {
    t
  } = useTranslation('blocks.donation');
  const isDisabled = error || !blockState.amount || isLoading;
  const checkoutUrl = checkoutUrlBuilder(campaignId, orgId, forwardCheckoutQueryParams, checkoutBaseUrl, {
    amount: blockState?.amount?.toFixed(2) ?? '0',
    frequency: blockState.frequency,
    recurringEndDate: blockState.recurringEndDate ?? '',
    currency: blockState.currencyCode ?? 'USD',
    designationId: programDesignation?.id?.toString() ?? ''
  }, blockState.dedicationText);
  const getButtonLabel = () => {
    if (!isLoading && blockState.amount && blockState.amount > 0 && !error) {
      return t('CallToActionButton.checkoutLinkButton.continue', 'Continue');
    }
    if (blockState.showDedication && blockState.dedicationError) {
      return t('CallToActionButton.cartButton.label.enterDedication', 'Enter a dedication');
    }
    return t('CallToActionButton.checkoutLinkButton.chooseAmount', 'Choose an amount');
  };
  return /*#__PURE__*/_jsx(Styled.CheckoutLinkButton, {
    href: checkoutUrl,
    isDisabled: isDisabled,
    "data-id": "cp-donation-form-donate",
    role: "button",
    children: getButtonLabel()
  });
};
/**
 * When gift catalog is enabled, render a button to add a donation item to the cart.
 * Otherwise, render a link to navigate to checkout.
 */
export const CallToActionButton = _ref3 => {
  let {
    blockState,
    campaignId,
    orgId,
    error = false,
    isCartEnabled,
    programDesignation,
    forwardCheckoutQueryParams,
    checkoutBaseUrl,
    isLoading
  } = _ref3;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: isCartEnabled && programDesignation ? /*#__PURE__*/_jsx(CartButton, {
      blockState: blockState,
      programDesignation: programDesignation,
      error: error,
      isLoading: isLoading
    }) : /*#__PURE__*/_jsx(CheckoutLinkButton, {
      blockState: blockState,
      campaignId: campaignId,
      orgId: orgId,
      error: error,
      forwardCheckoutQueryParams: forwardCheckoutQueryParams,
      checkoutBaseUrl: checkoutBaseUrl,
      isLoading: isLoading,
      programDesignation: programDesignation
    })
  });
};